/**
 * Shared logic for hardware unlock events (used by the rental and hardware
 * detail Unlocks tabs).
 *
 * Both `Rental.unlocks` (grouped per unlockable unit) and `Hardware.unlocks`
 * (flat) surface events of the same shape: `{id, timestamp, type}` with type
 * "attempt" or "success", oldest first.
 */

/**
 * An unlock attempt counts as successful when a success event for the same
 * hardware unit follows it within this many seconds. Matches the backend's
 * default `unlock_window` used by the unlock success rate calculations.
 */
export const UNLOCK_SUCCESS_WINDOW_SECONDS = 30;

/**
 * An unlock attempt also counts as successful when a success event for the
 * same hardware unit was recorded up to this many seconds BEFORE the attempt
 * (the success can land first, e.g. from event ordering/clock skew). Matches
 * the backend's default `backward_window` used by the unlock success rate
 * calculations.
 */
export const UNLOCK_SUCCESS_BACKWARD_WINDOW_SECONDS = 2;

/**
 * A single unlock event from an `unlocks` Json field.
 * The Json scalar arrives already parsed.
 */
export interface IUnlockEventJson {
  id: number | string;
  timestamp: string;
  type: 'attempt' | 'success' | string;
}

export interface IUnlockRow {
  id: string;
  timestamp: string;
  success: boolean;
  /**
   * Seconds from the attempt to its earliest matching success event;
   * negative when that success was recorded just before the attempt
   * (within the backward window)
   */
  secondsToUnlock: number | null;
}

function toMillis(event: IUnlockEventJson): number {
  return new Date(event.timestamp).getTime();
}

/**
 * Turns a unit's unlock events into display rows: one row per attempt with
 * its computed outcome, sorted chronologically. Success events only mark
 * attempts as successful; ones with no matching attempt are omitted.
 */
export function buildUnlockRows(events: readonly IUnlockEventJson[]): IUnlockRow[] {
  const attempts = events.filter((event) => event.type === 'attempt');
  const successes = events.filter((event) => event.type === 'success');
  const windowMs = UNLOCK_SUCCESS_WINDOW_SECONDS * 1000;
  const backwardWindowMs = UNLOCK_SUCCESS_BACKWARD_WINDOW_SECONDS * 1000;

  const rows: IUnlockRow[] = attempts.map((attempt) => {
    const attemptMs = toMillis(attempt);
    let secondsToUnlock: number | null = null;

    successes.forEach((success) => {
      const elapsedMs = toMillis(success) - attemptMs;
      if (elapsedMs < -backwardWindowMs || elapsedMs > windowMs) return;

      const elapsedSeconds = elapsedMs / 1000;
      if (secondsToUnlock === null || elapsedSeconds < secondsToUnlock) {
        secondsToUnlock = elapsedSeconds;
      }
    });

    return {
      id: String(attempt.id),
      timestamp: attempt.timestamp,
      success: secondsToUnlock !== null,
      secondsToUnlock,
    };
  });

  return rows.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

export function countFailedUnlockAttempts(rows: readonly IUnlockRow[]): number {
  return rows.filter((row) => !row.success).length;
}
